import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { LayoutDashboard, Users, FileText, DollarSign, Bell, CalendarCheck, ClipboardList, ScanLine, TestTube, LogOut, Menu, X, CalendarDays } from 'lucide-react';

const AdminLayout = () => {
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (adminUser) setAdmin(JSON.parse(adminUser));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: t.navDashboard, path: '/admin/dashboard' },
    { icon: Users, label: t.navPatients, path: '/admin/patients' },
    { icon: FileText, label: t.navTreatments, path: '/admin/treatments' },
    { icon: DollarSign, label: t.navPayments, path: '/admin/payments' },
    { icon: CalendarCheck, label: t.navAppointments, path: '/admin/appointments' },
    { icon: ClipboardList, label: t.navMedicalHistory, path: '/admin/medical-history' },
    { icon: ScanLine, label: t.navScans, path: '/admin/scans' },
    { icon: TestTube, label: t.navMedicalTests, path: '/admin/medical-tests' },
    { icon: Bell, label: t.navNotifications, path: '/admin/notifications' },
    { icon: CalendarDays, label: 'My Schedule', path: '/admin/schedule' },
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]" dir={t.dir} data-testid="admin-layout">
      <div className="bg-[#0F4C5C] text-white fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between px-4 lg:px-8 h-16">
          <div className="flex items-center">
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden mr-4" data-testid="admin-sidebar-toggle">
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>{t.adminPortal}</h1>
          </div>
          <div className="flex items-center space-x-4">
            {admin && (
              <div className={`hidden sm:block ${t.dir === 'rtl' ? 'text-left' : 'text-right'}`}>
                <div className="text-sm font-semibold">{admin.name}</div>
                <div className="text-xs text-white/80">{admin.email}</div>
              </div>
            )}
            <button onClick={handleLogout} className="flex items-center hover:text-[#98D8CA] transition-colors" data-testid="admin-logout-button">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex pt-16">
        <aside className={`fixed lg:sticky top-16 ${t.dir === 'rtl' ? 'right-0' : 'left-0'} h-[calc(100vh-4rem)] w-64 bg-white border-${t.dir === 'rtl' ? 'l' : 'r'} border-slate-200 transform transition-transform duration-300 z-30 ${sidebarOpen ? 'translate-x-0' : (t.dir === 'rtl' ? 'translate-x-full lg:translate-x-0' : '-translate-x-full lg:translate-x-0')}`} data-testid="admin-sidebar">
          <nav className="p-4 overflow-y-auto h-full pb-8">
            <div className="space-y-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-[#0F4C5C] text-white' : 'text-[#6B7280] hover:bg-[#F9FAFB]'}`}
                    data-testid={`admin-nav-${item.path.split('/').pop()}`}>
                    <item.icon className={`w-5 h-5 ${t.dir === 'rtl' ? 'ml-3' : 'mr-3'}`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </nav>
        </aside>
        <main className="flex-1 lg:ml-0"><Outlet /></main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} data-testid="admin-sidebar-overlay"></div>}
    </div>
  );
};

export default AdminLayout;
